/**
 * Credit Monitoring Provider Registry
 * 
 * SINGLE SOURCE OF TRUTH for all provider branding across the system.
 * 
 * Centralized configuration for all supported credit monitoring providers.
 * Makes it easy to add new providers, update referral links, and swap logos.
 * 
 * Fields:
 * - provider_key: Unique identifier for the provider (lowercase, normalized)
 * - provider_name: Display name
 * - provider_description: Short subtitle/description
 * - logo_src: Path to logo image/SVG asset
 * - referral_url: Affiliate/referral URL (null if not available)
 * - login_url: Direct login URL for "Open Provider Site" action
 * - provider_status: 'active' | 'coming_soon' | 'deprecated'
 * - supported_import_flow: Whether secure login import is supported
 * - badge_text: Optional badge like "Popular"
 * - sort_order: Display order in provider list
 * - brand_color: Primary brand color for fallback styling
 */

import React from 'react';
import { getBundledProviderLogoSrc, PROVIDER_LOGO_ASSETS } from './providerLogoAssets';
import { getProviderCapabilities } from './providerCapabilities';

// ============================================
// PROVIDER KEY NORMALIZATION
// Handles case variations: identityiq, IdentityIQ, IDENTITYIQ
// ============================================

export const normalizeProviderKey = (key) => {
  if (!key) return null;
  const compact = String(key).toLowerCase().trim();
  if (compact.replace(/[\s_-]/g, '') === 'creditheroscore') {
    return 'credit_hero_score';
  }
  return compact;
};

// ============================================
// PROVIDER LOGO COMPONENT
// Universal logo component that loads from asset path
// Handles logos with dark backgrounds by placing them in a styled container
// ============================================

export const ProviderLogo = ({
  src,
  alt,
  brandColor = '#1e40af',
  className = '',
  size = 'md', // 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}) => {
  const containerSizes = {
    xs: 'h-6 w-full max-w-[110px]',
    sm: 'h-8 w-full max-w-[130px]',
    md: 'h-10 w-full max-w-[170px]',
    lg: 'h-12 w-full max-w-[190px]',
    xl: 'h-14 w-full max-w-[220px]',
  };

  const [imgError, setImgError] = React.useState(false);
  const resolvedSrc = src || null;
  const containerClass = containerSizes[size] || containerSizes.md;

  React.useEffect(() => {
    setImgError(false);
  }, [resolvedSrc]);

  if (imgError || !resolvedSrc) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg font-bold text-white text-xs px-2 ${containerClass} ${className}`}
        style={{ backgroundColor: brandColor }}
        role="img"
        aria-label={alt}
      >
        <span className="truncate">{alt?.split(' ')[0] || 'Provider'}</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center ${containerClass} ${className}`}>
      <img
        src={resolvedSrc}
        alt={alt}
        className="max-h-full max-w-full h-full w-full object-contain object-left"
        style={{ objectFit: 'contain' }}
        onError={() => setImgError(true)}
        decoding="async"
      />
    </div>
  );
};

// ============================================
// INDIVIDUAL LOGO COMPONENTS (Config-Driven)
// These use the ProviderLogo component with specific assets
// ============================================

export const IdentityIQLogo = ({ className, size = 'md' }) => (
  <ProviderLogo
    src={PROVIDER_LOGO_ASSETS.identityiq}
    alt="IdentityIQ"
    brandColor="#e11d48"
    className={className}
    size={size}
  />
);

export const SmartCreditLogo = ({ className, size = 'md' }) => (
  <ProviderLogo
    src={PROVIDER_LOGO_ASSETS.smartcredit}
    alt="SmartCredit"
    brandColor="#059669"
    className={className}
    size={size}
  />
);

export const MyScoreIQLogo = ({ className, size = 'md' }) => (
  <ProviderLogo
    src={PROVIDER_LOGO_ASSETS.myscoreiq}
    alt="MyScoreIQ"
    brandColor="#22d3ee"
    className={className}
    size={size}
  />
);

export const MyFreeScoreNowLogo = ({ className, size = 'md' }) => (
  <ProviderLogo
    src={PROVIDER_LOGO_ASSETS.myfreescorenow}
    alt="MyFreeScoreNow"
    brandColor="#1e40af"
    className={className}
    size={size}
  />
);

export const PrivacyGuardLogo = ({ className, size = 'md' }) => (
  <ProviderLogo
    src={PROVIDER_LOGO_ASSETS.privacyguard}
    alt="PrivacyGuard"
    brandColor="#10b981"
    className={className}
    size={size}
  />
);

export const CreditHeroScoreLogo = ({ className, size = 'md' }) => (
  <ProviderLogo
    src={PROVIDER_LOGO_ASSETS.credit_hero_score}
    alt="CreditHeroScore"
    brandColor="#1e3a8a"
    className={className}
    size={size}
  />
);

// ============================================
// PROVIDER REGISTRY
// ============================================

export const PROVIDER_REGISTRY = {
  identityiq: {
    provider_key: 'identityiq',
    provider_name: 'IdentityIQ',
    provider_description: 'Real-time monitoring & alerts',
    LogoComponent: IdentityIQLogo,
    logo_src: PROVIDER_LOGO_ASSETS.identityiq,
    referral_url: 'https://www.identityiq.com/sc-securepreferred.aspx?offercode=431289D6',
    login_url: 'https://www.identityiq.com/login.aspx',
    create_account_url: 'https://www.identityiq.com/sc-securepreferred.aspx?offercode=431289D6',
    create_account_cta: "Don't have an IdentityIQ account? Create one",
    provider_status: 'active',
    supported_import_flow: true,
    badge_text: 'Popular',
    sort_order: 1,
    brand_color: '#e11d48',
    // Form field configuration
    form_fields: {
      username_label: 'Username or Email',
      username_placeholder: 'Enter your IdentityIQ username or email',
      password_label: 'Password',
      password_placeholder: 'Enter your IdentityIQ password',
      requires_ssn: false,
      requires_security_answer: false,
    },
  },
  credit_hero_score: {
    provider_key: 'credit_hero_score',
    provider_name: 'CreditHeroScore',
    provider_description: '3-bureau scores and full credit report',
    LogoComponent: CreditHeroScoreLogo,
    logo_src: PROVIDER_LOGO_ASSETS.credit_hero_score,
    referral_url: 'https://www.creditheroscore.com/',
    login_url: 'https://www.creditheroscore.com/customer_login.asp',
    create_account_url: 'https://www.creditheroscore.com/',
    create_account_cta: "Don't have a CreditHeroScore account? Create one",
    provider_status: 'partial',
    supported_import_flow: true,
    badge_text: 'Beta',
    sort_order: 2,
    brand_color: '#1e3a8a',
    form_fields: {
      username_label: 'Username or Email',
      username_placeholder: 'Enter your CreditHeroScore username or email',
      password_label: 'Password',
      password_placeholder: 'Enter your CreditHeroScore password',
      requires_ssn: false,
      requires_security_answer: false,
    },
  },
  smartcredit: {
    provider_key: 'smartcredit',
    provider_name: 'SmartCredit',
    provider_description: '3-bureau credit tracking',
    LogoComponent: SmartCreditLogo,
    logo_src: PROVIDER_LOGO_ASSETS.smartcredit,
    referral_url: 'https://www.smartcredit.com/join/?pid=86897',
    login_url: 'https://member.smartcredit.com/login',
    create_account_url: 'https://www.smartcredit.com/join/?pid=86897',
    create_account_cta: "Don't have a SmartCredit account? Create one",
    provider_status: 'active',
    supported_import_flow: true,
    badge_text: null,
    sort_order: 3,
    brand_color: '#059669',
    // Form field configuration
    form_fields: {
      username_label: 'Username or Email',
      username_placeholder: 'Enter your SmartCredit username or email',
      password_label: 'Password',
      password_placeholder: 'Enter your SmartCredit password',
      requires_ssn: false,
      requires_security_answer: false,
    },
  },
  myscoreiq: {
    provider_key: 'myscoreiq',
    provider_name: 'MyScoreIQ',
    provider_description: 'Credit score analysis',
    LogoComponent: MyScoreIQLogo,
    logo_src: PROVIDER_LOGO_ASSETS.myscoreiq,
    referral_url: 'https://www.myscoreiq.com/get-fico-preferred.aspx?offercode=432138XG',
    login_url: 'https://www.myscoreiq.com/login.aspx',
    create_account_url: 'https://www.myscoreiq.com/get-fico-preferred.aspx?offercode=432138XG',
    create_account_cta: "Don't have a MyScoreIQ account? Create one",
    provider_status: 'active',
    supported_import_flow: true,
    badge_text: null,
    sort_order: 4,
    brand_color: '#22d3ee',
    // Form field configuration
    form_fields: {
      username_label: 'Username or Email',
      username_placeholder: 'Enter your MyScoreIQ username or email',
      password_label: 'Password',
      password_placeholder: 'Enter your MyScoreIQ password',
      requires_ssn: false,
      requires_security_answer: false,
    },
  },
  myfreescorenow: {
    provider_key: 'myfreescorenow',
    provider_name: 'MyFreeScoreNow',
    provider_description: 'Credit monitoring access',
    LogoComponent: MyFreeScoreNowLogo,
    logo_src: PROVIDER_LOGO_ASSETS.myfreescorenow,
    referral_url: 'https://app.myfreescorenow.com/enroll/d01?s1=&s=MXxmYWxzZQ%3D%3D',
    login_url: 'https://www.myfreescorenow.com/login',
    create_account_url: 'https://app.myfreescorenow.com/enroll/d01?s1=&s=MXxmYWxzZQ%3D%3D',
    create_account_cta: "Don't have a MyFreeScoreNow account? Create one",
    provider_status: 'active',
    supported_import_flow: true,
    badge_text: null,
    sort_order: 5,
    brand_color: '#1e40af',
    // Form field configuration
    form_fields: {
      username_label: 'Username or Email',
      username_placeholder: 'Enter your MyFreeScoreNow username or email',
      password_label: 'Password',
      password_placeholder: 'Enter your MyFreeScoreNow password',
      requires_ssn: false,
      requires_security_answer: false,
    },
  },
  privacyguard: {
    provider_key: 'privacyguard',
    provider_name: 'PrivacyGuard',
    provider_description: 'Identity protection suite',
    LogoComponent: PrivacyGuardLogo,
    logo_src: PROVIDER_LOGO_ASSETS.privacyguard,
    referral_url: 'https://www.privacyguard.com/plans-pricing.html',
    login_url: 'https://www.privacyguard.com/login.aspx',
    create_account_url: 'https://www.privacyguard.com/plans-pricing.html',
    create_account_cta: "Don't have a PrivacyGuard account? Create one",
    provider_status: 'active',
    supported_import_flow: true,
    badge_text: null,
    sort_order: 6,
    brand_color: '#10b981',
    // Form field configuration
    form_fields: {
      username_label: 'Username or Email',
      username_placeholder: 'Enter your PrivacyGuard username or email',
      password_label: 'Password',
      password_placeholder: 'Enter your PrivacyGuard password',
      requires_ssn: false,
      requires_security_answer: false,
    },
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get bundled logo URL for a provider (same source as ProviderLogo / cards).
 */
export const getProviderLogoSrc = (providerKey) => getBundledProviderLogoSrc(providerKey);

/**
 * Get all active providers sorted by sort_order
 */
export const getActiveProviders = () => {
  return Object.values(PROVIDER_REGISTRY)
    .filter((p) => (
      p.provider_status === 'active'
      || (p.provider_status === 'partial' && p.supported_import_flow)
    ))
    .sort((a, b) => a.sort_order - b.sort_order);
};

/**
 * Get a provider by key (handles case variations)
 */
export const getProvider = (providerKey) => {
  const normalized = normalizeProviderKey(providerKey);
  return PROVIDER_REGISTRY[normalized] || null;
};

/**
 * Get the appropriate URL for a provider (referral if available, else login)
 */
export const getProviderUrl = (providerKey) => {
  const provider = getProvider(providerKey);
  if (!provider) return null;
  return provider.referral_url || provider.login_url;
};

/**
 * Check if a provider has a referral URL
 */
export const hasReferralUrl = (providerKey) => {
  const provider = getProvider(providerKey);
  return provider?.referral_url !== null;
};

/**
 * Get the create account URL for a provider
 */
export const getCreateAccountUrl = (providerKey) => {
  const provider = getProvider(providerKey);
  return provider?.create_account_url || null;
};

/**
 * Get the create account CTA text for a provider
 */
export const getCreateAccountCta = (providerKey) => {
  const provider = getProvider(providerKey);
  return provider?.create_account_cta || `Don't have an account? Create one`;
};

/**
 * Get form field configuration for a provider
 */
export const getProviderFormConfig = (providerKey) => {
  const provider = getProvider(providerKey);
  const caps = getProviderCapabilities(providerKey);
  const defaults = {
    username_label: 'Username or Email',
    username_placeholder: provider
      ? `Enter your ${provider.provider_name} username or email`
      : 'Enter your username or email',
    password_label: 'Password',
    password_placeholder: provider
      ? `Enter your ${provider.provider_name} password`
      : 'Enter your password',
    requires_ssn: Boolean(caps.requiresLastFourSsn),
    requires_security_answer: Boolean(caps.requiresSecurityAnswer),
    requires_security_question: Boolean(caps.requiresSecurityQuestion),
  };
  return {
    ...defaults,
    ...(provider?.form_fields || {}),
    requires_ssn: Boolean(caps.requiresLastFourSsn),
    requires_security_answer: Boolean(caps.requiresSecurityAnswer),
    requires_security_question: Boolean(caps.requiresSecurityQuestion),
  };
};

/**
 * Render provider logo with standardized SVG
 * This is the main function to use across the system
 * 
 * @param {string} providerKey - Provider identifier (handles case variations)
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} className - Additional CSS classes
 * @returns {React.ReactElement} - Logo component or fallback
 */
export const renderProviderLogo = (providerKey, size = 'md', className = '') => {
  const provider = getProvider(providerKey);
  
  if (!provider) {
    // Fallback for unknown providers
    return (
      <div 
        className={`flex items-center justify-center rounded-lg bg-stone-200 text-stone-600 font-semibold px-3 py-2 ${className}`}
      >
        {providerKey?.charAt(0)?.toUpperCase() || '?'}
      </div>
    );
  }
  
  const LogoComponent = provider.LogoComponent;
  return <LogoComponent size={size} className={className} />;
};

/**
 * Get provider display info for use in lists/cards
 * 
 * @param {string} providerKey - Provider identifier
 * @returns {object} - { name, description, logo_src, brand_color, LogoComponent }
 */
export const getProviderDisplayInfo = (providerKey) => {
  const provider = getProvider(providerKey);
  
  if (!provider) {
    return {
      name: providerKey || 'Unknown Provider',
      description: 'Provider not found',
      logo_src: null,
      brand_color: '#6b7280',
      LogoComponent: null,
    };
  }
  
  return {
    name: provider.provider_name,
    description: provider.provider_description,
    logo_src: provider.logo_src,
    brand_color: provider.brand_color,
    LogoComponent: provider.LogoComponent,
    badge_text: provider.badge_text,
    status: provider.provider_status,
    create_account_url: provider.create_account_url,
    create_account_cta: provider.create_account_cta,
    form_fields: provider.form_fields,
  };
};

export default PROVIDER_REGISTRY;
